import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { calendarAPI } from '../utils/api';
import { format, addDays, type Locale } from 'date-fns';
import { ru, enUS, es } from 'date-fns/locale';
import './CalendarPage.scss';

const LOCALE_MAP: Record<string, Locale> = { ru, en: enUS, es };

type DayLevel = 'favorable' | 'neutral' | 'risky';

interface DailyScore {
  date: string;
  score: number;
  level: DayLevel;
  recommendation: string;
  categoryInsights: {
    decisions: string;
    relationships: string;
    work: string;
    finances: string;
    personalChanges: string;
  };
}

const LEVEL_CLASS: Record<DayLevel, string> = {
  favorable: 'calendar-day--favorable',
  neutral: 'calendar-day--neutral',
  risky: 'calendar-day--risky',
};

const CalendarPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = LOCALE_MAP[i18n.language] || enUS;

  const getLevelLabel = (level: DayLevel) => t(`calendar.level.${level}`);
  const navigate = useNavigate();
  const [scores, setScores] = useState<DailyScore[]>([]);
  const [today, setToday] = useState<DailyScore | null>(null);
  const [limitDays, setLimitDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [horizon, setHorizon] = useState<'7' | '30' | '365'>('7');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    loadScores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, horizon, i18n.language]);

  const loadScores = async () => {
    setLoading(true);
    setError(null);
    try {
      const from = format(new Date(), 'yyyy-MM-dd');
      const days = horizon === '7' ? 7 : horizon === '30' ? 30 : 365;
      const to = format(addDays(new Date(), days - 1), 'yyyy-MM-dd');
      const res = await calendarAPI.getScores(from, to, i18n.language);
      setScores(res.data.scores || []);
      setToday(res.data.today || null);
      setLimitDays(res.data.limitDays ?? 7);
    } catch (err: any) {
      const apiError = err.response?.data?.error;
      const msg = apiError === 'No matrix data'
        ? t('calendar.errors.noMatrix')
        : (err.response?.data?.message || err.response?.data?.error || t('calendar.errors.load'));
      setError(msg);
      setScores([]);
      setToday(null);
    } finally {
      setLoading(false);
    }
  };

  const selectedScore = selectedDate ? scores.find((s) => s.date === selectedDate) : today;

  const categories = selectedScore
    ? [
        { key: 'decisions', label: t('calendar.categories.decisions'), text: selectedScore.categoryInsights.decisions },
        { key: 'relationships', label: t('calendar.categories.relationships'), text: selectedScore.categoryInsights.relationships },
        { key: 'work', label: t('calendar.categories.work'), text: selectedScore.categoryInsights.work },
        { key: 'finances', label: t('calendar.categories.finances'), text: selectedScore.categoryInsights.finances },
        { key: 'personalChanges', label: t('calendar.categories.personalChanges'), text: selectedScore.categoryInsights.personalChanges },
      ]
    : [];

  return (
    <div className="calendar-page">
      <div className="calendar-page__container">
        <header className="calendar-page__header">
          <button type="button" className="page-back" onClick={() => navigate('/dashboard')} aria-label={t('nav.back')}>
            ← {t('nav.back')}
          </button>
          <h1 className="calendar-page__title">{t('calendar.title')}</h1>
        </header>

        {loading && <p className="calendar-page__loading">{t('calendar.loading')}</p>}
        {error && (
          <div className="calendar-page__error">
            <p>{error}</p>
            <button type="button" className="btn-secondary" onClick={() => navigate('/dashboard')}>
              {t('calendar.createMatrixFirst')}
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="calendar-page__horizon">
              <span>{t('calendar.period')}:</span>
              {[7, 30, 365].filter((d) => d <= limitDays).length > 0 ? (
                [7, 30, 365]
                  .filter((d) => d <= limitDays)
                  .map((d) => (
                    <button
                      key={d}
                      type="button"
                      className={horizon === String(d) ? 'btn-primary' : 'btn-secondary'}
                      onClick={() => setHorizon(d === 365 ? '365' : d === 30 ? '30' : '7')}
                    >
                      {d === 7 ? t('calendar.days7') : d === 30 ? t('calendar.days30') : t('calendar.days365')}
                    </button>
                  ))
              ) : (
                <button type="button" className="btn-primary" disabled>
                  {limitDays} {t('calendar.days')}
                </button>
              )}
            </div>

            <div className="calendar-page__grid">
              {scores.map((day) => (
                <button
                  key={day.date}
                  type="button"
                  className={`calendar-page__day ${LEVEL_CLASS[day.level]} ${selectedDate === day.date ? 'calendar-page__day--selected' : ''}`}
                  onClick={() => setSelectedDate(day.date)}
                  title={`${day.score} — ${getLevelLabel(day.level)}`}
                >
                  <span className="calendar-page__day-date">{format(new Date(day.date), 'd MMM', { locale: dateLocale })}</span>
                  <span className="calendar-page__day-score">{day.score}</span>
                  <span className="calendar-page__day-level">{getLevelLabel(day.level)}</span>
                </button>
              ))}
            </div>

            {selectedScore && (
              <section className="calendar-page__detail" aria-labelledby="calendar-detail-heading">
                <h2 id="calendar-detail-heading">
                  {selectedDate ? format(new Date(selectedDate), 'd MMMM yyyy', { locale: dateLocale }) : t('calendar.today')}
                </h2>
                <p className="calendar-page__detail-index">
                  {t('calendar.index')}: {selectedScore.score} — {getLevelLabel(selectedScore.level)}
                </p>
                <p className="calendar-page__detail-recommendation">{selectedScore.recommendation}</p>
                <ul className="calendar-page__detail-categories">
                  {categories.map((c) => (
                    <li key={c.key}>
                      <strong>{c.label}</strong>: {c.text}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {limitDays < 365 && (
              <p className="calendar-page__upgrade">
                {t('calendar.upgrade')}{' '}
                <button type="button" className="link" onClick={() => navigate('/subscription')}>
                  {t('calendar.subscription')}
                </button>
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CalendarPage;
